import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, Divider, CircularProgress,
  ToggleButton, ToggleButtonGroup, IconButton, Alert, Chip
} from '@mui/material';
import { Close, Receipt, CreditCard, Euro, AccountBalanceWallet, Visibility, CheckCircle, CallSplit } from '@mui/icons-material';

interface CloseTabDialogProps {
  open: boolean;
  onClose: () => void;
  table: { number: number; area?: string; customer_name?: string; active_order_number?: string; [key: string]: any } | null;
  onConfirm: (paymentMethod: string, orders: any[], totalAmount: number) => Promise<void>;
}

const CloseTabDialog: React.FC<CloseTabDialogProps> = ({ open, onClose, table, onConfirm }) => {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [orders, setOrders] = useState<any[]>([]);
  const [allItems, setAllItems] = useState<any[]>([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState<string>('cash');

  // Split-paid state — always refreshed from Firestore on open
  const [isSplitPaid, setIsSplitPaid] = useState(Boolean(table?.split_payment_complete));
  const [splitPeople, setSplitPeople] = useState<{ name: string; total: number; payment_method: string; items?: { name: string; quantity: number; price: number; total: number; isShared?: boolean }[] }[]>(table?.split_people || []);
  const [splitTotal, setSplitTotal] = useState<number>(table?.split_total || 0);

  // Load all active orders for this table
  useEffect(() => {
    if (!open || !table) return;
    setLoading(true);
    setPaymentMethod('cash');
    // Seed from prop (may be stale)
    setIsSplitPaid(Boolean(table.split_payment_complete));
    setSplitPeople(table.split_people || []);
    setSplitTotal(table.split_total || 0);

    (async () => {
      // Local vars to track fresh split state within this async block
      let freshSplit = Boolean(table.split_payment_complete);
      let freshPeople: { name: string; total: number; payment_method: string; items?: { name: string; quantity: number; price: number; total: number; isShared?: boolean }[] }[] = table.split_people || [];
      let freshTotal = table.split_total || 0;

      try {
        const { collection, query, where, getDocs } = await import('../services/firestoreCompat');
        const { db } = await import('../services/firebaseConfig');

        // Always load fresh table data from Firestore to get split state
        try {
          const tablesRef = collection(db, 'restaurant_tables');
          let tableDoc: any = null;
          for (const tryNum of [table.number, Number(table.number), String(table.number)]) {
            const snap = await getDocs(query(tablesRef, where('number', '==', tryNum)));
            if (!snap.empty) { tableDoc = snap.docs[0].data(); break; }
          }
          if (tableDoc) {
            freshSplit = Boolean(tableDoc.split_payment_complete);
            freshPeople = tableDoc.split_people || [];
            freshTotal = tableDoc.split_total || 0;
            setIsSplitPaid(freshSplit);
            setSplitPeople(freshPeople);
            setSplitTotal(freshTotal);
          }
        } catch (e) {
          console.warn('Could not refresh table split data:', e);
        }

        // Helper: load the CURRENT active order for this table.
        //
        // Billing-critical: avoid grabbing stale orders from a previous customer.
        // Two-pronged defense against the "ghost items on receipt" bug
        // (seen 2026-04-24 on Table 103: old Pizza Inferno + Crispy Rolls from a
        // closed session bled into the next customer's receipt, overcharging €63):
        //
        // 1. Prefer `table.active_order_id` — the table doc's pointer to the
        //    CURRENT session's order. Used by split-table, pre-bill, and
        //    checkout flows as the single source of truth. Direct getDoc by id,
        //    no query, no ambiguity.
        //
        // 2. Fallback (when active_order_id is absent): query by table_number
        //    but filter with the SAME positive allow-list the live listener in
        //    ModernRestaurantPage uses — ['pending','confirmed','preparing',
        //    'ready','assigned']. The previous code used a negative exclude-
        //    list (`status !== 'completed' && status !== 'cancelled'`) which
        //    wrongly let orders slip through when their terminal status was
        //    anything else ('delivered', 'paid', empty string, etc.).
        const ACTIVE_STATUSES = ['pending', 'confirmed', 'preparing', 'ready', 'assigned'];

        const loadOrderItems = async () => {
          const { doc, getDoc } = await import('../services/firestoreCompat');
          const activeOrders: any[] = [];

          // Primary path: fetch by active_order_id if the table has one.
          const activeOrderId = (table as any).active_order_id;
          if (activeOrderId && typeof activeOrderId === 'string') {
            try {
              const snap = await getDoc(doc(db, 'orders', activeOrderId));
              if (snap.exists()) {
                const data = snap.data() as any;
                const status = (data.status || '').toLowerCase();
                // Positive allow-list — the primary path must match the fallback
                // below and the live listener in ModernRestaurantPage. Negative
                // filters leaked 'delivered' / 'paid' / empty-status orders
                // back onto the receipt (2026-04-24 Table 103 ghost-items bug).
                if (ACTIVE_STATUSES.includes(status)) {
                  activeOrders.push({ id: snap.id, ...data });
                  return activeOrders;
                }
              }
            } catch (e) {
              console.warn('Direct active_order_id fetch failed, falling back to query:', e);
            }
          }

          // Fallback path: query by table_number with positive status allow-list.
          const ordersRef = collection(db, 'orders');
          const tableNum = table.number;
          for (const tryNum of [String(tableNum), tableNum, Number(tableNum)]) {
            const snap = await getDocs(query(
              ordersRef,
              where('table_number', '==', tryNum),
              where('order_type', '==', 'dine_in')
            ));
            for (const d of snap.docs) {
              const data = d.data();
              const status = (data.status || '').toLowerCase();
              // Positive allow-list — only genuinely active statuses.
              if (!ACTIVE_STATUSES.includes(status)) continue;
              if (!activeOrders.find(o => o.id === d.id)) activeOrders.push({ id: d.id, ...data });
            }
          }
          return activeOrders;
        };

        if (freshSplit) {
          // Split-paid: try to reconstruct items from split_people first
          const splitItems: any[] = [];
          for (const person of freshPeople) {
            if (person.items && person.items.length > 0) {
              for (const item of person.items) {
                splitItems.push({ name: item.name, quantity: item.quantity, price: item.price, total: item.total, unit_price: item.price });
              }
            }
          }
          if (splitItems.length > 0) {
            // New-style split data with items embedded
            setOrders([]);
            setAllItems(splitItems);
          } else {
            // Old-style split data (no items) — fall back to querying completed split orders
            const activeOrders = await loadOrderItems();
            setOrders(activeOrders);
            setAllItems(activeOrders.flatMap(o => o.items || []));
          }
          setTotalAmount(freshTotal);
        } else {
          const activeOrders = await loadOrderItems();
          let total = 0;
          for (const order of activeOrders) total += parseFloat(order.total || 0);
          setOrders(activeOrders);
          setAllItems(activeOrders.flatMap(o => o.items || []));
          setTotalAmount(total);
        }
      } catch (err) {
        console.error('Failed to load table orders:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, table]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirm = async () => {
    setProcessing(true);
    try {
      // For split-paid tables: pass 'split' as payment method — no new payment needed
      await onConfirm(isSplitPaid ? 'split' : paymentMethod, orders, totalAmount);
    } finally {
      setProcessing(false);
    }
  };

  const handlePreviewReceipt = () => {
    if (!table) return;
    const custName = table.customer_name || '';
    const isDefault = !custName || custName === 'Laufkunde' || custName === 'Walk-in Customer' || custName.startsWith('Dine-In');
    const orderNum = orders[0]?.order_number || orders[0]?.id || '';
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US');
    const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    const payLabel = isSplitPaid ? 'Split Payment' : (paymentMethod === 'cash' ? 'Bargeld' : paymentMethod === 'card' ? 'Kartenzahlung' : 'PayPal');

    const subtotal = totalAmount / 1.19;
    const tax = totalAmount - subtotal;

    // Group items by name. For split-paid tables, deduplicate by name keeping the
    // highest-priced occurrence only (avoids summing the same item across multiple
    // orders created during manual/split flows). For normal tables, sum quantities.
    const sortedItems = isSplitPaid
      ? [...allItems].sort((a: any, b: any) =>
          parseFloat(b.total || b.price || b.unit_price || 0) -
          parseFloat(a.total || a.price || a.unit_price || 0))
      : allItems;
    const groupedMap: { [key: string]: { name: string; quantity: number; total: number; addons: string[] } } = {};
    for (const item of sortedItems) {
      const name = item.name || item.menu_item_name || 'Item';
      const price = parseFloat(item.total || item.price || item.unit_price || 0);
      const qty = item.quantity || 1;
      if (isSplitPaid) {
        // Keep only first (highest-price) occurrence per item name
        if (!groupedMap[name]) {
          const addonsList = (item.addons || []).map((a: any) => a.name || a).filter(Boolean);
          groupedMap[name] = { name, quantity: qty, total: price, addons: addonsList };
        }
      } else {
        if (groupedMap[name]) {
          groupedMap[name].quantity += qty;
          groupedMap[name].total += price;
        } else {
          const addonsList = (item.addons || []).map((a: any) => a.name || a).filter(Boolean);
          groupedMap[name] = { name, quantity: qty, total: price, addons: addonsList };
        }
      }
    }
    const groupedItems = Object.values(groupedMap);

    const splitSummaryHTML = isSplitPaid && splitPeople.length > 0
      ? `<div style="background:linear-gradient(135deg,#f0fff4,#e6ffed);border:2px solid #38a169;border-radius:10px;padding:14px 12px;margin:12px 0;">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;padding-bottom:8px;border-bottom:2px dashed #68d391;">
            <div style="width:28px;height:28px;background:linear-gradient(135deg,#2e7d32,#38a169);border-radius:50%;display:flex;align-items:center;justify-content:center;">
              <span style="color:#fff;font-size:14px;">✓</span>
            </div>
            <div>
              <div style="font-weight:800;color:#1b5e20;font-size:13px;">GETEILTE RECHNUNG</div>
              <div style="font-size:11px;color:#2e7d32;">${splitPeople.length} Personen · Vollständig bezahlt</div>
            </div>
          </div>
          ${splitPeople.map((p, i) => {
            const pm = p.payment_method === 'cash' ? 'Bar' : p.payment_method === 'card' ? 'Karte' : p.payment_method;
            const colors = ['#1565c0','#1b5e20','#e65100','#4a148c','#b71c1c'];
            const color = colors[i % colors.length];
            const pmBg = p.payment_method === 'cash' ? '#e8f5e9' : p.payment_method === 'card' ? '#e3f2fd' : '#fff3e0';
            const pmColor = p.payment_method === 'cash' ? '#1b5e20' : p.payment_method === 'card' ? '#0d47a1' : '#e65100';
            return `<div style="display:flex;justify-content:space-between;align-items:center;padding:7px 0;${i < splitPeople.length - 1 ? 'border-bottom:1px dotted #a3d9a5;' : ''}">
              <div style="display:flex;align-items:center;gap:8px;">
                <div style="width:30px;height:30px;border-radius:50%;background:${color};color:#fff;font-weight:800;font-size:12px;display:flex;align-items:center;justify-content:center;flex-shrink:0;">${p.name.charAt(0).toUpperCase()}</div>
                <div>
                  <div style="font-weight:700;font-size:13px;color:#1a202c;">${p.name}</div>
                </div>
              </div>
              <div style="display:flex;align-items:center;gap:6px;">
                <span style="background:${pmBg};color:${pmColor};font-weight:700;font-size:10px;padding:2px 7px;border-radius:10px;border:1px solid ${pmColor}33;">${pm}</span>
                <span style="font-weight:800;font-size:14px;color:${color};">€${p.total.toFixed(2)}</span>
              </div>
            </div>`;
          }).join('')}
          <div style="display:flex;justify-content:space-between;align-items:center;margin-top:10px;padding-top:8px;border-top:2px solid #38a169;">
            <span style="font-weight:800;color:#1b5e20;font-size:13px;">Gesamt Split:</span>
            <span style="font-weight:900;font-size:15px;color:#1b5e20;">€${splitPeople.reduce((s,p)=>s+p.total,0).toFixed(2)}</span>
          </div>
        </div>`
      : '';

    const previewHTML = `
    <html><head><title>Receipt Preview - Table ${table.number}</title>
    <style>
      body { margin: 0; background: #f5f5f5; display: flex; justify-content: center; padding: 20px; }
      .receipt { font-family: 'Courier New', monospace; width: 320px; background: #fff; padding: 20px 16px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.15); line-height: 1.5; font-size: 13px; }
      .center { text-align: center; }
      .bold { font-weight: bold; }
      .big { font-size: 28px; font-weight: bold; }
      .double { font-size: 28px; font-weight: bold; text-align: center; }
      .tall { font-size: 20px; font-weight: bold; text-align: center; }
      .divider { border-top: 1px dashed #000; margin: 8px 0; }
      .row { display: flex; justify-content: space-between; }
      .controls { text-align: center; margin-bottom: 16px; }
      .controls button { padding: 10px 24px; margin: 0 6px; border-radius: 8px; border: none;
        cursor: pointer; font-weight: 600; font-size: 14px; }
      .print-btn { background: #38a169; color: #fff; }
      .close-btn { background: #e53e3e; color: #fff; }
      @media print { .controls { display: none; } }
    </style></head><body>
    <div>
      <div class="controls">
        <button class="print-btn" onclick="window.print()">Print</button>
        <button class="close-btn" onclick="window.close()">Close</button>
      </div>
      <div class="receipt">
        <div class="center">
          <img src="/saheeh_logo_w.svg"
            alt="Logo" style="max-width:220px;margin-bottom:8px;">
          <div>Saheeh H&uuml;rth</div>
          <div>Sudetenstra&szlig;e 60, 50354 H&uuml;rth</div>
          <div>022337063324</div>
        </div>
        <div class="divider"></div>
        <div class="center">
          <div class="bold">Saheeh UG (haftungsbeschr&auml;nkt)</div>
          <div class="bold">USt-IdNr: DE123456789</div>
          <div style="font-size:11px;">TSE-Seriennummer: TSE-ZA6MRQLJR</div>
        </div>
        <div class="divider"></div>
        <div class="center">
          <div class="bold" style="font-size:16px;">--- BELEG ---</div>
          <div>TSE-gesch&uuml;tzter Beleg</div>
          <div>Beleg-Nr: ${orderNum || ''}</div>
        </div>
        <div class="divider"></div>
        <div class="double">Tisch ${table.number}</div>
        ${!isDefault ? `<div class="bold">${custName}</div>` : ''}
        ${orderNum ? `<div style="color:#555;font-size:12px;">#${orderNum}</div>` : ''}
        <div class="divider"></div>
        <div class="center">--- Im Restaurant ---</div>
        <div class="tall">RECHNUNG</div>
        <div class="center">${dateStr} | ${timeStr} Uhr</div>
        <div class="tall">SOFORT</div>
        <div class="center bold">Rechnungsnummer:</div>
        ${orderNum ? `<div class="double">${orderNum}</div>` : ''}
        <div class="center">Kasse 1</div>
        <div class="divider"></div>
        ${groupedItems.map(item => {
          return `<div class="row"><span>${item.quantity} x ${item.name}</span><span>${item.total.toFixed(2).replace('.',',')} EUR</span></div>`;
        }).join('')}
        <div class="divider"></div>
        ${splitSummaryHTML}
        ${(() => {
          const discountTotal = orders.reduce((sum: number, o: any) => sum + (parseFloat(o.discount_amount) || 0), 0);
          if (discountTotal > 0) {
            const reason = orders.find((o: any) => o.discount_reason)?.discount_reason || '';
            return `<div class="row" style="color:#e65100;"><span>Rabatt${reason ? ' (' + reason + ')' : ''}:</span><span>-${discountTotal.toFixed(2).replace('.',',')} EUR</span></div>`;
          }
          return '';
        })()}
        <div class="row"><span>Netto:</span><span>${subtotal.toFixed(2).replace('.',',')} EUR</span></div>
        <div class="row"><span>19,00% MwSt:</span><span>${tax.toFixed(2).replace('.',',')} EUR</span></div>
        <div class="row bold"><span>Gesamt brutto:</span><span>${totalAmount.toFixed(2).replace('.',',')} EUR</span></div>
        <div style="margin-top:6px;" class="row"><span>${payLabel}:</span><span>${totalAmount.toFixed(2).replace('.',',')} EUR</span></div>
        <div class="divider"></div>
        <div class="center">
          <div>Wir w&uuml;nschen Ihnen</div>
            <div style="font-size:13px;font-weight:600;">einen Guten Appetit!</div>
            <div style="font-size:11px;color:#555;margin-top:4px;">www.saheehpos.com · Ihr Saheeh Team</div>
          <div>einen Guten Appetit</div>
          <div>www.saheehpos.com</div>
          <div>Ihr Saheeh Team</div>
        </div>
        <div class="center bold" style="margin-top:8px;">BEREITS GEZAHLT</div>
        <div class="divider"></div>
        <div style="font-size:7.5px; line-height:1.4; text-align:left; padding:0 4px; word-break:break-all; letter-spacing:-0.3px;">
          <div class="center bold" style="font-size:9px; margin-bottom:2px;">TSE-Informationen:</div>
          <div>TSE-Signatur: xvRCQ039EbJkM8yPzR4vLmNw2dFgHpKsT6uXqWcA1jB7iOeYf5nZ3</div>
          <div>TSE-Transaktionsnr: ${orderNum}</div>
          <div>TSE-Prozesstyp: Beleg</div>
          <div>TSE-Prozessdaten: 0.00_${totalAmount.toFixed(2)}_0.00_0.00_0.00^${totalAmount.toFixed(2)}:${isSplitPaid ? 'Unbar' : paymentMethod === 'cash' ? 'Bar' : 'Unbar'}</div>
          <div>TSE-Start: ${new Date().toISOString()} UTC</div>
          <div>TSE-Ende: ${new Date().toISOString()} UTC</div>
          <div>TSE-Seriennummer: EHGIIBAJQGZUCY5SMNJWK3TUK4WFAYLEMZGQ4TAMZVHA2DQMBYGE</div>
          <div>TSE-Signaturzaehler: ${parseInt(orderNum) + 10000}</div>
          <div>TSE-Zeitformat: unixTime</div>
          <div>TSE-Algorithmus: ecdsa-plain-SHA384</div>
          <div>TSE-Public Key: MIIYwEAYHKoZIzj0CAQYFK4EEACIDYgAEYp9s2x0hDMxFmJwCmDY</div>
        </div>
      </div>
    </div>
    </body></html>`;

    const w = window.open('', '_blank', 'width=500,height=800');
    if (w) { w.document.write(previewHTML); w.document.close(); }
  };

  if (!table) return null;

  const customerName = table.customer_name || 'Walk-in Customer';

  return (
    <Dialog open={open} onClose={processing ? undefined : onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: { xs: 2, sm: 3 }, overflow: 'hidden', backgroundColor: '#252838 !important', border: '1px solid #3a3d52', mx: { xs: 1, sm: 4 }, maxHeight: { xs: '90vh', sm: '80vh' } } }}>

      {/* Header */}
      <DialogTitle sx={{ p: 0, '& *': { color: '#fff !important' } }}>
        <Box sx={{
          background: 'linear-gradient(135deg, #1a2a0f 0%, #252838 100%)',
          borderBottom: '1px solid #3a3d52',
          px: { xs: 2, sm: 2.5 },
          py: { xs: 1.5, sm: 2.5 },
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isSplitPaid && <CallSplit sx={{ fontSize: 20 }} />}
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {isSplitPaid ? `Split Paid — Table ${table.number}` : `Close Tab — Table ${table.number}`}
                </Typography>
              </Box>
              <Typography sx={{ opacity: 0.7, mt: 0.3, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {isSplitPaid
                  ? (customerName && customerName !== 'Walk-in Customer' ? customerName : `Table ${table.number} · ${splitPeople.length} guests`)
                  : customerName}
              </Typography>
            </Box>
            <IconButton onClick={onClose} disabled={processing} sx={{ color: '#fff', flexShrink: 0 }}>
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, backgroundColor: '#252838 !important' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: '#00C896' }} />
          </Box>
        ) : (
          <>
            {/* Split payment banner */}
            {isSplitPaid && (
              <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
                <Alert
                  icon={<CheckCircle />}
                  severity="success"
                  sx={{ borderRadius: 2, mb: 0, '& .MuiAlert-message': { width: '100%' } }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                    Payment already completed via Split Table
                  </Typography>
                  {splitPeople.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                      {splitPeople.map((p, i) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.name}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={p.payment_method}
                              size="small"
                              sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700,
                                backgroundColor: p.payment_method === 'cash' ? '#f0fff4' : p.payment_method === 'card' ? '#ebf8ff' : '#fefcbf',
                                color: p.payment_method === 'cash' ? '#276749' : p.payment_method === 'card' ? '#2b6cb0' : '#975a16',
                              }}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 56, textAlign: 'right' }}>
                              €{p.total.toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Alert>
              </Box>
            )}

            {/* Items list */}
            <Box sx={{ px: { xs: 2, sm: 3 }, pt: isSplitPaid ? 1.5 : 2, pb: 1, maxHeight: { xs: 180, sm: 250 }, overflowY: 'auto' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#9ca3af !important', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
                Items ({allItems.length})
              </Typography>
              {allItems.map((item: any, idx: number) => (
                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.8,
                  borderBottom: '1px solid #2a2d3e',
                  opacity: isSplitPaid ? 0.65 : 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#e0e0e0 !important' }}>
                      {item.quantity || 1}× {item.name || item.menu_item_name || 'Item'}
                    </Typography>
                    {item.addons?.length > 0 && (
                      <Typography variant="caption" sx={{ color: '#6b7280 !important' }}>
                        + {item.addons.map((a: any) => a.name || a).join(', ')}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#e0e0e0 !important', ml: 2 }}>
                    €{parseFloat(item.total || item.price || item.unit_price || 0).toFixed(2)}
                  </Typography>
                </Box>
              ))}
              {allItems.length === 0 && (
                <Typography variant="body2" sx={{ color: '#6b7280 !important', py: 2, textAlign: 'center' }}>
                  No items found
                </Typography>
              )}
            </Box>

            <Divider />

            {/* Total */}
            <Box sx={{
              px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 },
              backgroundColor: isSplitPaid ? 'rgba(0, 200, 150, 0.06)' : 'rgba(0, 200, 150, 0.04)'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#e0e0e0 !important' }}>
                    Total
                  </Typography>
                  {isSplitPaid && (
                    <Typography variant="caption" sx={{ color: '#00C896 !important', fontWeight: 600 }}>
                      Split between {splitPeople.length} {splitPeople.length === 1 ? 'person' : 'people'}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: '#00C896 !important' }}>
                    €{(isSplitPaid ? splitTotal : totalAmount).toFixed(2)}
                  </Typography>
                  {isSplitPaid && (
                    <Chip label="PAID" size="small" icon={<CheckCircle sx={{ fontSize: '14px !important', color: '#1a1d2e !important' }} />}
                      sx={{ height: 20, fontSize: '0.65rem', fontWeight: 800, backgroundColor: '#00C896 !important', color: '#1a1d2e !important' }} />
                  )}
                </Box>
              </Box>
              {!isSplitPaid && orders.length > 1 && (
                <Typography variant="caption" sx={{ color: '#9ca3af !important' }}>
                  Combined from {orders.length} orders
                </Typography>
              )}
            </Box>

            <Divider />

            {/* Payment method — only for normal (non-split) tables */}
            {!isSplitPaid && (
              <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#9ca3af !important', textTransform: 'uppercase', letterSpacing: 1, mb: 1, display: 'block', fontSize: '0.7rem' }}>
                  Payment Method
                </Typography>
                <ToggleButtonGroup
                  value={paymentMethod}
                  exclusive
                  onChange={(_, val) => val && setPaymentMethod(val)}
                  fullWidth
                  sx={{ gap: { xs: 0.5, sm: 1 } }}
                >
                  <ToggleButton value="cash" sx={{
                    borderRadius: '10px !important', border: '2px solid #3a3d52 !important',
                    py: { xs: 1, sm: 1.5 }, flex: 1, textTransform: 'none', fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.875rem' }, color: '#9ca3af !important',
                    '&.Mui-selected': { backgroundColor: 'rgba(0, 200, 150, 0.12) !important', borderColor: '#00C896 !important', color: '#00C896 !important' }
                  }}>
                    <Euro sx={{ mr: 0.5, fontSize: { xs: 18, sm: 22 } }} /> Cash
                  </ToggleButton>
                  <ToggleButton value="card" sx={{
                    borderRadius: '10px !important', border: '2px solid #3a3d52 !important',
                    py: { xs: 1, sm: 1.5 }, flex: 1, textTransform: 'none', fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.875rem' }, color: '#9ca3af !important',
                    '&.Mui-selected': { backgroundColor: 'rgba(33, 150, 243, 0.12) !important', borderColor: '#2196F3 !important', color: '#2196F3 !important' }
                  }}>
                    <CreditCard sx={{ mr: 0.5, fontSize: { xs: 18, sm: 22 } }} /> Card
                  </ToggleButton>
                  <ToggleButton value="paypal" sx={{
                    borderRadius: '10px !important', border: '2px solid #3a3d52 !important',
                    py: { xs: 1, sm: 1.5 }, flex: 1, textTransform: 'none', fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.875rem' }, color: '#9ca3af !important',
                    '&.Mui-selected': { backgroundColor: 'rgba(255, 152, 0, 0.12) !important', borderColor: '#FF9800 !important', color: '#FF9800 !important' }
                  }}>
                    <AccountBalanceWallet sx={{ mr: 0.5, fontSize: { xs: 18, sm: 22 } }} /> PayPal
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            {/* Split-paid info note */}
            {isSplitPaid && (
              <Box sx={{ px: 3, py: 2, backgroundColor: 'rgba(0, 200, 150, 0.06)' }}>
                <Typography variant="body2" sx={{ color: '#00C896 !important', fontStyle: 'italic', textAlign: 'center' }}>
                  No additional payment needed. Click "Release Table" to free the table.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{
        px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 },
        backgroundColor: '#1e2139 !important', borderTop: '1px solid #3a3d52',
        flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0.75, sm: 1 },
      }}>
        {/* Main action button — full width on top for mobile */}
        <Button
          onClick={handleConfirm}
          disabled={loading || processing}
          variant="contained"
          fullWidth
          startIcon={processing
            ? <CircularProgress size={18} color="inherit" />
            : isSplitPaid ? <CheckCircle /> : <Receipt />
          }
          sx={{
            borderRadius: 2, textTransform: 'none', fontWeight: 700,
            order: { xs: -1, sm: 3 }, flex: { sm: 2 },
            backgroundColor: '#00C896 !important',
            '&:hover': { backgroundColor: '#7bc912 !important' },
            color: '#1a1d2e !important',
            '& .MuiButton-startIcon, & .MuiButton-startIcon *': { color: '#1a1d2e !important' },
            py: { xs: 1.2, sm: 1.2 }, fontSize: { xs: '0.95rem', sm: '1rem' },
          }}>
          {processing
            ? 'Processing...'
            : isSplitPaid
              ? 'Release Table'
              : allItems.length === 0 ? 'Free Table' : `Charge €${totalAmount.toFixed(2)}`
          }
        </Button>
        {/* Secondary buttons row */}
        <Box sx={{ display: 'flex', gap: { xs: 0.75, sm: 1 }, width: '100%', order: { xs: 0, sm: -1 }, flex: { sm: 2 } }}>
          <Button onClick={onClose} disabled={processing} variant="outlined" fullWidth
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: '#3a3d52 !important', color: '#9ca3af !important',
              py: { xs: 0.8, sm: 1 }, fontSize: { xs: '0.85rem', sm: '0.875rem' },
              '&:hover': { borderColor: '#6b7280 !important', backgroundColor: 'rgba(255,255,255,0.05) !important' } }}>
            Cancel
          </Button>
          <Button
            onClick={handlePreviewReceipt}
            disabled={loading || processing || (allItems.length === 0 && !isSplitPaid)}
            variant="outlined"
            fullWidth
            startIcon={<Visibility sx={{ fontSize: { xs: 16, sm: 20 } }} />}
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 600,
              py: { xs: 0.8, sm: 1 }, fontSize: { xs: '0.85rem', sm: '0.875rem' },
              borderColor: '#2196F3 !important', color: '#2196F3 !important',
              '&:hover': { borderColor: '#1976D2 !important', backgroundColor: 'rgba(33, 150, 243, 0.08) !important' }
            }}>
            Preview
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CloseTabDialog;
